import { useEffect } from 'react'

/**
 * Drag-to-zoom on genome-wide bedGraph wig tracks (coverage / BAF / CNV depth),
 * GEN-5709.
 *
 * IGV.js only offers drag-to-zoom on the top ruler (its RulerSweeper); on a
 * track body a plain drag pans. Reviewers reading the SeqOne-style chromosome
 * plots (coverage repartition, B-allele frequency, depth ratio) want to sweep a
 * span directly on the plot and zoom the whole browser to it. This hook adds
 * that gesture, scoped to the genome-wide wig viewports so alignment / variant
 * tracks and the ruler keep their native behavior.
 *
 * On mousedown inside a matching viewport we capture the viewport's reference
 * frame; on mousemove we draw a translucent selection overlay; on mouseup we
 * convert the pixel span to base pairs via the reference frame and call
 * browser.goto(chr, start, end). Drags narrower than a threshold fall through
 * as a click (IGV shows its feature popup), so this does not swallow clicks.
 */

/** Minimum horizontal drag, in px, that counts as a zoom rather than a click. */
export const DRAG_ZOOM_MIN_PX = 5

type ReferenceFrame = {
  chr: string
  start: number
  bpPerPixel: number
  toBP: (pixels: number) => number
}

type IgvViewport = {
  viewportElement: HTMLElement
  referenceFrame: ReferenceFrame
}

type IgvTrackView = {
  track?: { id?: string; name?: string; config?: { id?: string; name?: string } }
  viewports?: IgvViewport[]
}

export type DragZoomBrowser = {
  trackViews?: IgvTrackView[]
  search?: (locus: string) => unknown
}

/**
 * True when a track id marks a genome-wide bedGraph plot we make sweepable.
 * The API gives these stable id suffixes -coverage / -baf / -depth (see
 * igvTracks.ts / variant-lens-api discovery.py), which toIgvTrack threads
 * through to the igv.js track id. Matching the id, not the human label, avoids
 * the depth track being missed because its label reads "... depth ratio"
 * rather than ending in "depth" (GEN-5709).
 */
export function isDragZoomTrackId(id: string | undefined | null): boolean {
  if (!id) return false
  return /-(coverage|baf|depth)$/i.test(id.trim())
}

/** Resolve the IGV viewport (referenceFrame + track id) owning a DOM node. */
function findViewport(
  browser: DragZoomBrowser,
  element: HTMLElement | null
): { viewport: IgvViewport; trackId: string | undefined } | null {
  const host = element?.closest('.igv-viewport') as HTMLElement | null
  if (!host || !browser.trackViews) return null
  for (const trackView of browser.trackViews) {
    for (const viewport of trackView.viewports ?? []) {
      if (viewport.viewportElement === host) {
        return { viewport, trackId: trackView.track?.id ?? trackView.track?.config?.id }
      }
    }
  }
  return null
}

/**
 * Convert a pixel span within a viewport to a normalized 1-based, inclusive
 * genomic interval, clamped so start >= 1 and start <= end. Returns null when
 * the reference frame is a whole-genome ("all") view, where a bp span is
 * meaningless.
 */
export function spanToLocus(
  referenceFrame: ReferenceFrame,
  x1: number,
  x2: number
): { chr: string; start: number; end: number } | null {
  if (!referenceFrame || referenceFrame.chr.toLowerCase() === 'all') return null
  const left = Math.min(x1, x2)
  const right = Math.max(x1, x2)
  const startBp = Math.floor(referenceFrame.start + referenceFrame.toBP(left))
  const endBp = Math.ceil(referenceFrame.start + referenceFrame.toBP(right))
  const start = Math.max(1, startBp + 1) // 0-based frame -> 1-based locus
  const end = Math.max(start, endBp)
  return { chr: referenceFrame.chr, start, end }
}

function makeOverlay(): HTMLDivElement {
  const el = document.createElement('div')
  el.style.position = 'absolute'
  el.style.top = '0'
  el.style.bottom = '0'
  el.style.pointerEvents = 'none'
  el.style.background = 'rgba(70,130,180,0.2)'
  el.style.borderLeft = '1px solid rgba(70,130,180,0.8)'
  el.style.borderRight = '1px solid rgba(70,130,180,0.8)'
  el.style.zIndex = '512'
  el.setAttribute('data-testid', 'igv-drag-zoom-overlay')
  return el
}

/**
 * Attach the drag-to-zoom gesture to the browser's genome-wide wig viewports.
 * No-op until the browser is ready. Cleans up its listeners and overlay on
 * unmount or when the browser instance changes.
 */
export function useTrackDragZoom(browser: DragZoomBrowser | null, ready: boolean, host: HTMLElement | null): void {
  useEffect(() => {
    if (!ready || !browser || !host) return

    // On a matching wig viewport this hook fully owns the pointer gesture: it
    // claims mousedown (so IGV never starts a pan and the reference frame never
    // shifts under us) and decides click vs drag itself. A plain click still
    // opens IGV's feature popup because IGV fires that on the synthetic `click`
    // event, which we forward for clicks and swallow for drags. Snapshotting the
    // frame's start/bpPerPixel at mousedown keeps the pixel->bp mapping stable
    // for the whole sweep (GEN-5709 / Bugbot).
    let gesture: {
      viewportEl: HTMLElement
      startChr: string
      startBp: number
      bpPerPixel: number
      startX: number
      dragging: boolean
      overlay: HTMLDivElement | null
    } | null = null
    // True from the end of a drag until the ensuing `click` fires, so we can
    // swallow that one click (a drag must not also open a popup).
    let swallowNextClick = false

    const localX = (viewportEl: HTMLElement, clientX: number): number =>
      clientX - viewportEl.getBoundingClientRect().left

    const endGesture = () => {
      gesture?.overlay?.remove()
      gesture = null
    }

    const onMouseDown = (event: MouseEvent) => {
      // Clear any stale swallow flag first: if a prior drag ended outside `host`
      // no click fired to reset it, and we must not suppress this gesture's
      // click (Bugbot). Do this before any early return.
      swallowNextClick = false
      if (event.button !== 0) return
      const match = findViewport(browser, event.target as HTMLElement | null)
      if (!match || !isDragZoomTrackId(match.trackId)) return
      const frame = match.viewport.referenceFrame
      if (frame.chr.toLowerCase() === 'all') return
      const viewportEl = match.viewport.viewportElement
      gesture = {
        viewportEl,
        // Snapshot the frame now: owning mousedown stops IGV panning, but this
        // also guards against any later mutation of the live frame.
        startChr: frame.chr,
        startBp: frame.start,
        bpPerPixel: frame.bpPerPixel,
        startX: localX(viewportEl, event.clientX),
        dragging: false,
        overlay: null,
      }
      // Own the gesture: block IGV's pan (which would start on mousedown and
      // shift the frame). The synthetic `click` still fires for a plain click.
      event.preventDefault()
      event.stopPropagation()
    }

    const onMouseMove = (event: MouseEvent) => {
      if (!gesture) return
      const x = localX(gesture.viewportEl, event.clientX)
      if (!gesture.dragging) {
        if (Math.abs(x - gesture.startX) < DRAG_ZOOM_MIN_PX) return // still a click
        gesture.dragging = true
        gesture.overlay = makeOverlay()
        gesture.viewportEl.appendChild(gesture.overlay)
      }
      const left = Math.min(gesture.startX, x)
      const width = Math.abs(x - gesture.startX)
      if (gesture.overlay) {
        gesture.overlay.style.left = `${left}px`
        gesture.overlay.style.width = `${width}px`
      }
      event.preventDefault()
      event.stopPropagation()
    }

    const onMouseUp = (event: MouseEvent) => {
      if (!gesture) return
      const current = gesture
      endGesture()
      if (!current.dragging) return // plain click: let the `click` reach IGV
      event.preventDefault()
      event.stopPropagation()
      swallowNextClick = true // suppress the click that follows this drag
      const endX = localX(current.viewportEl, event.clientX)
      // Map pixels to bp with the snapshotted frame (immune to any pan drift).
      const startBp = Math.floor(current.startBp + Math.min(current.startX, endX) * current.bpPerPixel)
      const endBp = Math.ceil(current.startBp + Math.max(current.startX, endX) * current.bpPerPixel)
      const start = Math.max(1, startBp + 1) // 0-based frame -> 1-based locus
      const end = Math.max(start, endBp)
      // Navigate via the documented `search` API (igv.js 3.8), matching how the
      // rest of IgvMount changes locus rather than the thin `goto` wrapper.
      browser.search?.(`${current.startChr}:${start}-${end}`)
    }

    const onClick = (event: MouseEvent) => {
      if (!swallowNextClick) return
      swallowNextClick = false
      event.preventDefault()
      event.stopPropagation()
    }

    // Capture phase so we intercept before IGV's viewport handlers.
    host.addEventListener('mousedown', onMouseDown, true)
    window.addEventListener('mousemove', onMouseMove, true)
    window.addEventListener('mouseup', onMouseUp, true)
    host.addEventListener('click', onClick, true)

    return () => {
      host.removeEventListener('mousedown', onMouseDown, true)
      window.removeEventListener('mousemove', onMouseMove, true)
      window.removeEventListener('mouseup', onMouseUp, true)
      host.removeEventListener('click', onClick, true)
      endGesture()
    }
  }, [browser, ready, host])
}
